import { useEffect, useState } from "react";
import { Plus, X, Loader2, Search, Calendar, Phone } from "lucide-react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { NoShowRiskBadge } from "@/components/atomic-crm/misc/NoShowRiskBadge";
import { SendReminderButton } from "@/components/atomic-crm/misc/SendReminderButton";

const supabase = getSupabaseClient();

type AppStatus =
  | "scheduled"
  | "completed"
  | "cancelled"
  | "no_show"
  | "rescheduled";

type Appointment = {
  id: string;
  patientName: string;
  phoneNumber: string;
  appointmentType: string;
  dateTime: string;
  duration: string;
  provider: string;
  status: AppStatus;
  notes: string;
  sentiment: string | null;
};

const STATUS_CFG: Record<
  AppStatus,
  { dot: string; text: string; bg: string; label: string }
> = {
  scheduled: {
    dot: "#2563EB",
    text: "#1D4ED8",
    bg: "#DBEAFE",
    label: "Scheduled",
  },
  completed: {
    dot: "#16A34A",
    text: "#15803D",
    bg: "#DCFCE7",
    label: "Completed",
  },
  cancelled: {
    dot: "#9CA3AF",
    text: "#6B7280",
    bg: "#F3F4F6",
    label: "Cancelled",
  },
  no_show: { dot: "#DC2626", text: "#B91C1C", bg: "#FEE2E2", label: "No-Show" },
  rescheduled: {
    dot: "#D97706",
    text: "#92400E",
    bg: "#FEF3C7",
    label: "Rescheduled",
  },
};

const StatusBadge = ({ status }: { status: AppStatus }) => {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.scheduled;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 100,
        background: cfg.bg,
        color: cfg.text,
        letterSpacing: "0.02em",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
};

const fmtDate = (dt: string) =>
  new Date(dt).toLocaleDateString("en-US", { month: "short", day: "numeric" });
const fmtTime = (dt: string) =>
  new Date(dt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const fmtDateLong = (dt: string) =>
  new Date(dt).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
const fmtPhone = (p: string) => {
  if (!p) return p;
  const d = p.replace(/\D/g, "");
  if (d.length === 10)
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return p;
};

const mapRow = (row: any): Appointment => {
  const dateStr = row.appointment_date || "";
  const timeStr = row.appointment_time || "00:00";
  const dateTime = dateStr
    ? `${dateStr}T${timeStr}`
    : row.created_at || new Date().toISOString();
  const rawStatus = (row.status || "scheduled").toLowerCase();
  const status: AppStatus = (
    ["scheduled", "completed", "cancelled", "no_show", "rescheduled"].includes(
      rawStatus,
    )
      ? rawStatus
      : "scheduled"
  ) as AppStatus;
  return {
    id: row.id,
    patientName: row.patient_name || row.caller_name || "Unknown",
    phoneNumber: row.phone_number || "",
    appointmentType: row.reason_for_visit || row.appointment_type || "General",
    dateTime,
    duration: row.duration || "30 min",
    provider: row.provider || "Clinic Staff",
    status,
    notes: row.notes || "",
    sentiment: row.conversations?.sentiment ?? null,
  };
};

// ── Create Sheet ─────────────────────────────────────────────────────────────

const CreateSheet = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [form, setForm] = useState({
    patientName: "",
    phone: "",
    appointmentType: "Initial Evaluation",
    date: "",
    time: "09:00",
    duration: "60",
    provider: "",
    notes: "",
  });
  const [creating, setCreating] = useState(false);

  const handle = async () => {
    if (!form.patientName || !form.date) return;
    setCreating(true);
    const { error } = await supabase.from("appointments").insert({
      patient_name: form.patientName,
      phone_number: form.phone || null,
      appointment_type: form.appointmentType,
      appointment_date: form.date,
      appointment_time: form.time,
      duration: `${form.duration} min`,
      provider: form.provider || "Clinic Staff",
      notes: form.notes || null,
      status: "scheduled",
    });
    setCreating(false);
    if (!error) {
      setForm({
        patientName: "",
        phone: "",
        appointmentType: "Initial Evaluation",
        date: "",
        time: "09:00",
        duration: "60",
        provider: "",
        notes: "",
      });
      onCreated();
      onClose();
    }
  };

  if (!open) return null;

  const fields = [
    {
      label: "Patient name *",
      key: "patientName",
      type: "text",
      placeholder: "Jane Smith",
    },
    {
      label: "Phone",
      key: "phone",
      type: "tel",
      placeholder: "+1 (555) 000-0000",
    },
    {
      label: "Appointment type",
      key: "appointmentType",
      type: "text",
      placeholder: "Initial Evaluation",
    },
    { label: "Date *", key: "date", type: "date", placeholder: "" },
    { label: "Time", key: "time", type: "time", placeholder: "" },
    {
      label: "Duration (min)",
      key: "duration",
      type: "number",
      placeholder: "60",
    },
    {
      label: "Provider",
      key: "provider",
      type: "text",
      placeholder: "Dr. Smith",
    },
  ] as const;

  const inputStyle = {
    width: "100%",
    fontSize: 13,
    padding: "8px 12px",
    background: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: 7,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box" as const,
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex" }}>
      <div
        style={{ flex: 1, background: "rgba(0,0,0,0.2)" }}
        onClick={onClose}
      />
      <div
        style={{
          width: 384,
          height: "100%",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          background: "#FFFFFF",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid #E8E8E8",
          }}
        >
          <p style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>
            New appointment
          </p>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9CA3AF",
              padding: 4,
              borderRadius: 6,
              display: "flex",
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div
          style={{
            flex: 1,
            padding: "18px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {fields.map(({ label, key, type, placeholder }) => (
            <div key={key}>
              <label
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#6B7280",
                  marginBottom: 5,
                }}
              >
                {label}
              </label>
              <input
                type={type}
                value={form[key as keyof typeof form]}
                onChange={(e) =>
                  setForm((f) => ({ ...f, [key]: e.target.value }))
                }
                placeholder={placeholder}
                style={inputStyle}
              />
            </div>
          ))}
          <div>
            <label
              style={{
                display: "block",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#6B7280",
                marginBottom: 5,
              }}
            >
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              rows={3}
              style={{ ...inputStyle, resize: "none" }}
            />
          </div>
        </div>
        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid #E8E8E8",
            display: "flex",
            gap: 8,
          }}
        >
          <button
            onClick={handle}
            disabled={creating || !form.patientName || !form.date}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              padding: "9px 0",
              borderRadius: 7,
              fontSize: 13,
              fontWeight: 600,
              background: "#111111",
              color: "#FFFFFF",
              border: "none",
              cursor: "pointer",
              opacity: creating || !form.patientName || !form.date ? 0.5 : 1,
            }}
          >
            {creating && (
              <Loader2
                style={{
                  width: 14,
                  height: 14,
                  animation: "spin 1s linear infinite",
                }}
              />
            )}
            {creating ? "Saving…" : "Save appointment"}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "9px 14px",
              borderRadius: 7,
              fontSize: 13,
              background: "#F9FAFB",
              color: "#374151",
              border: "1px solid #E8E8E8",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Detail Panel ─────────────────────────────────────────────────────────────

const DetailPanel = ({
  apt,
  onMarkComplete,
  onMarkNoShow,
}: {
  apt: Appointment;
  onMarkComplete: (id: string) => void;
  onMarkNoShow: (id: string) => void;
}) => {
  const [linkedSummary, setLinkedSummary] = useState<string | null>(null);
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(
    apt.dateTime.split("T")[0],
  );
  const [rescheduleTime, setRescheduleTime] = useState(
    apt.dateTime.split("T")[1]?.slice(0, 5) ?? "",
  );
  const isScheduled = apt.status === "scheduled";

  useEffect(() => {
    setRescheduleMode(false);
    setLinkedSummary(null);
    if (!apt.phoneNumber) return;
    const phone = apt.phoneNumber.replace(/\D/g, "").slice(-10);
    if (!phone) return;
    supabase
      .from("conversations")
      .select("id,summary")
      .ilike("phone_number", `%${phone}%`)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.summary) setLinkedSummary(data.summary);
      });
  }, [apt.id, apt.phoneNumber]);

  const handleReschedule = async () => {
    if (!rescheduleDate) return;
    const { error } = await supabase
      .from("appointments")
      .update({
        appointment_date: rescheduleDate,
        appointment_time: rescheduleTime || null,
        status: "rescheduled",
      })
      .eq("id", apt.id);
    if (!error) {
      setRescheduleMode(false);
      window.location.reload();
    }
  };

  const inputStyle = {
    fontSize: 12,
    padding: "7px 10px",
    background: "#FFFFFF",
    border: "1px solid #D1D5DB",
    borderRadius: 7,
    color: "#111827",
    outline: "none",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        background: "#FFFFFF",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 22px 14px",
          borderBottom: "1px solid #E8E8E8",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div>
            <p
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: "#111827",
                letterSpacing: "-0.02em",
                marginBottom: 3,
              }}
            >
              {apt.patientName}
            </p>
            {apt.phoneNumber && (
              <a
                href={`tel:${apt.phoneNumber}`}
                style={{
                  fontSize: 13,
                  color: "#111111",
                  textDecoration: "none",
                  fontFamily: "monospace",
                }}
              >
                {fmtPhone(apt.phoneNumber)}
              </a>
            )}
          </div>
          <StatusBadge status={apt.status} />
        </div>

        <div
          style={{
            padding: "12px 14px",
            background: "#F9FAFB",
            border: "1px solid #E8E8E8",
            borderRadius: 9,
            marginBottom: 10,
          }}
        >
          <p
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#9CA3AF",
              marginBottom: 4,
            }}
          >
            Appointment
          </p>
          <p
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.02em",
              marginBottom: 2,
            }}
          >
            {fmtDateLong(apt.dateTime)}
          </p>
          <p
            style={{
              fontSize: 13,
              color: "#6B7280",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtTime(apt.dateTime)} · {apt.duration}
          </p>
        </div>

        <div
          style={{
            background: "#FFFFFF",
            border: "1px solid #E8E8E8",
            borderRadius: 9,
            overflow: "hidden",
          }}
        >
          {[
            { label: "Type", value: apt.appointmentType },
            { label: "Provider", value: apt.provider },
          ].map((row, i) => (
            <div
              key={row.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 13px",
                borderBottom: i === 0 ? "1px solid #E8E8E8" : "none",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#9CA3AF",
                }}
              >
                {row.label}
              </span>
              <span style={{ fontSize: 13, color: "#374151" }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>

        {isScheduled && apt.sentiment && (
          <div style={{ marginTop: 8 }}>
            <NoShowRiskBadge
              sentiment={apt.sentiment}
              isEveningAppointment={new Date(apt.dateTime).getHours() >= 17}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "14px 22px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {(linkedSummary || apt.notes) && (
          <div
            style={{
              padding: "12px 14px",
              background: "#F0FDF4",
              border: "1px solid #BBF7D0",
              borderRadius: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 7,
              }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: "#DCFCE7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: 8, color: "#111111" }}>✦</span>
              </div>
              <p
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#111111",
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                }}
              >
                AI Booking Notes
              </p>
            </div>
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65 }}>
              {linkedSummary || apt.notes}
            </p>
          </div>
        )}

        {rescheduleMode && (
          <div
            style={{
              background: "#F9FAFB",
              border: "1px solid #E8E8E8",
              borderRadius: 9,
              padding: "14px 16px",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "#6B7280",
                marginBottom: 10,
              }}
            >
              Reschedule to
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={handleReschedule}
                style={{
                  flex: 1,
                  padding: "7px 0",
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  background: "#111111",
                  color: "#FFFFFF",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => setRescheduleMode(false)}
                style={{
                  padding: "7px 12px",
                  borderRadius: 7,
                  fontSize: 12,
                  background: "#F3F4F6",
                  color: "#6B7280",
                  border: "1px solid #E8E8E8",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {isScheduled && (
        <div
          style={{
            padding: "14px 22px",
            borderTop: "1px solid #E8E8E8",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => onMarkComplete(apt.id)}
            style={{
              width: "100%",
              padding: "10px 0",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              background: "#111111",
              color: "#FFFFFF",
              border: "none",
              cursor: "pointer",
            }}
          >
            Mark Complete
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => onMarkNoShow(apt.id)}
              style={{
                flex: 1,
                padding: "9px 0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                background: "#FFFFFF",
                color: "#DC2626",
                border: "1px solid #FECACA",
                cursor: "pointer",
              }}
            >
              No-Show
            </button>
            <button
              onClick={() => setRescheduleMode(!rescheduleMode)}
              style={{
                flex: 1,
                padding: "9px 0",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                background: "#F9FAFB",
                color: "#374151",
                border: "1px solid #E8E8E8",
                cursor: "pointer",
              }}
            >
              Reschedule
            </button>
          </div>
          <SendReminderButton
            appointmentId={apt.id}
            patientName={apt.patientName}
            phoneNumber={apt.phoneNumber}
            appointmentDate={apt.dateTime.split("T")[0]}
            appointmentTime={apt.dateTime.split("T")[1]?.slice(0, 5) ?? ""}
          />
        </div>
      )}
    </div>
  );
};

// ── Appointment Row ──────────────────────────────────────────────────────────

const AppointmentRow = ({
  apt,
  selected,
  onClick,
}: {
  apt: Appointment;
  selected: boolean;
  onClick: () => void;
}) => {
  const cfg = STATUS_CFG[apt.status] ?? STATUS_CFG.scheduled;
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 14px",
        background: selected ? "#F0FDF4" : "#FFFFFF",
        border: "none",
        borderLeft: selected ? "2px solid #111111" : "2px solid transparent",
        borderBottom: "1px solid #E8E8E8",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#F9FAFB";
      }}
      onMouseLeave={(e) => {
        if (!selected)
          (e.currentTarget as HTMLElement).style.background = "#FFFFFF";
      }}
    >
      <div style={{ width: 56, flexShrink: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 2,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: cfg.dot,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#111827",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtTime(apt.dateTime)}
          </span>
        </div>
        <p style={{ fontSize: 10, color: "#9CA3AF", paddingLeft: 13 }}>
          {fmtDate(apt.dateTime)}
        </p>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            letterSpacing: "-0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            marginBottom: 2,
          }}
        >
          {apt.patientName}
        </p>
        <p
          style={{
            fontSize: 11,
            color: "#9CA3AF",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {apt.appointmentType}
        </p>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <p style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 2 }}>
          {apt.provider}
        </p>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: "2px 6px",
            borderRadius: 4,
            background: "#F3F4F6",
            color: "#6B7280",
          }}
        >
          {apt.duration}
        </span>
      </div>
    </button>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

type StatusFilter = "all" | "scheduled" | "completed" | "no_show" | "cancelled";
type DateFilter = "today" | "week" | "month" | "all";

export const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("week");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const fetchAppointments = async () => {
    try {
      const { data } = await supabase
        .from("appointments")
        .select("*, conversations(sentiment)")
        .order("appointment_date", { ascending: true });
      setAppointments((data || []).map(mapRow));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleMarkComplete = async (id: string) => {
    const { error } = await supabase
      .from("appointments")
      .update({ status: "completed" })
      .eq("id", id);
    if (!error)
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, status: "completed" as AppStatus } : a,
        ),
      );
  };

  const handleMarkNoShow = async (id: string) => {
    const { error } = await supabase
      .from("appointments")
      .update({ status: "no_show" })
      .eq("id", id);
    if (!error)
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === id ? { ...a, status: "no_show" as AppStatus } : a,
        ),
      );
  };

  const todayStr = new Date().toISOString().split("T")[0];
  const weekEndStr = new Date(Date.now() + 7 * 86400000)
    .toISOString()
    .split("T")[0];
  const monthEndStr = new Date(Date.now() + 30 * 86400000)
    .toISOString()
    .split("T")[0];

  const visible = appointments.filter((a) => {
    const d = a.dateTime.split("T")[0];
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (dateFilter === "today" && d !== todayStr) return false;
    if (dateFilter === "week" && (d < todayStr || d > weekEndStr)) return false;
    if (dateFilter === "month" && (d < todayStr || d > monthEndStr))
      return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !a.patientName.toLowerCase().includes(q) &&
        !a.phoneNumber.includes(q) &&
        !a.appointmentType.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const selectedApt = appointments.find((a) => a.id === selectedId) ?? null;

  const STATUS_PILLS: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "scheduled", label: "Scheduled" },
    { key: "completed", label: "Completed" },
    { key: "no_show", label: "No-Show" },
    { key: "cancelled", label: "Cancelled" },
  ];

  const DATE_PILLS: { key: DateFilter; label: string }[] = [
    { key: "today", label: "Today" },
    { key: "week", label: "This week" },
    { key: "month", label: "This month" },
    { key: "all", label: "All" },
  ];

  const pillStyle = (active: boolean, green?: boolean) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 10px",
    borderRadius: 100,
    background: active ? "#111111" : "transparent",
    color: active ? "#FFFFFF" : "#555555",
    border: "1px solid",
    borderColor: active ? "#111111" : "transparent",
    cursor: "pointer" as const,
  });

  return (
    <div
      style={{
        display: "flex",
        height: "calc(100vh - 56px)",
        background: "#F7F7F7",
        overflow: "hidden",
      }}
    >
      {/* Left: appointment list */}
      <div
        style={{
          flex: 1,
          borderRight: "1px solid #E8E8E8",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
          background: "#FFFFFF",
        }}
      >
        {/* Filters */}
        <div
          style={{
            padding: "14px 14px 12px",
            borderBottom: "1px solid #E8E8E8",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#111827",
                  letterSpacing: "-0.02em",
                }}
              >
                Appointments
              </p>
              <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 1 }}>
                {visible.length} showing
              </p>
            </div>
            <button
              onClick={() => setCreateOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12,
                fontWeight: 600,
                padding: "7px 12px",
                borderRadius: 7,
                background: "#111111",
                color: "#FFFFFF",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(22,163,74,0.3)",
              }}
            >
              <Plus style={{ width: 12, height: 12 }} />
              New
            </button>
          </div>

          <div style={{ position: "relative" }}>
            <Search
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 13,
                height: 13,
                color: "#9CA3AF",
              }}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search patient, type…"
              style={{
                width: "100%",
                fontSize: 13,
                background: "#F9FAFB",
                border: "1px solid #E8E8E8",
                borderRadius: 7,
                paddingLeft: 30,
                paddingRight: 12,
                paddingTop: 7,
                paddingBottom: 7,
                color: "#111827",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {STATUS_PILLS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={pillStyle(statusFilter === key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 4 }}>
            {DATE_PILLS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setDateFilter(key)}
                style={pillStyle(dateFilter === key, true)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div
              style={{
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 54,
                    borderRadius: 7,
                    background: "#F3F4F6",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 56,
                gap: 8,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#F3F4F6",
                  border: "1px solid #E8E8E8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Calendar style={{ width: 16, height: 16, color: "#D1D5DB" }} />
              </div>
              <p style={{ fontSize: 13, fontWeight: 500, color: "#6B7280" }}>
                No appointments
              </p>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>
                {statusFilter !== "all" || dateFilter !== "all"
                  ? "Try clearing filters."
                  : "Sera books them automatically."}
              </p>
            </div>
          ) : (
            visible.map((apt) => (
              <AppointmentRow
                key={apt.id}
                apt={apt}
                selected={selectedId === apt.id}
                onClick={() =>
                  setSelectedId(selectedId === apt.id ? null : apt.id)
                }
              />
            ))
          )}
        </div>
      </div>

      {/* Right: detail panel */}
      <div
        style={{
          width: 380,
          flexShrink: 0,
          height: "100%",
          overflow: "hidden",
        }}
      >
        {selectedApt ? (
          <DetailPanel
            key={selectedApt.id}
            apt={selectedApt}
            onMarkComplete={handleMarkComplete}
            onMarkNoShow={handleMarkNoShow}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 10,
              background: "#F7F7F7",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#FFFFFF",
                border: "1px solid #E8E8E8",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Calendar style={{ width: 20, height: 20, color: "#D1D5DB" }} />
            </div>
            <p style={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
              Select an appointment
            </p>
            <p style={{ fontSize: 12, color: "#9CA3AF" }}>
              Click any row to view details
            </p>
          </div>
        )}
      </div>

      <CreateSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchAppointments}
      />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      `}</style>
    </div>
  );
};

export default AppointmentsPage;
